(function (scope) {
  'use strict';

  // A collection card prints the product's lowest price above zero ("from" when prices vary).
  // The badge uses that same price. compare_at_price counts only when it belongs to a variant
  // at that price and is strictly higher. A bundle markdown is the same check: the bundle
  // variant's own compare_at_price, not a product-level compare_at from a different variant,
  // and not a $0 variant.
  function realCardSavings(variants, options) {
    const saveType = options && options.saveType === 'percent' ? 'percent' : 'dollar';
    const scoped = Array.isArray(variants) ? variants : [];
    const priced = scoped.filter(function (variant) {
      return variant && Number(variant.price) > 0;
    });
    if (!priced.length) return null;
    const shown = priced.reduce(function (min, variant) {
      const price = Number(variant.price);
      return price < min ? price : min;
    }, Number(priced[0].price));
    let saved = null;
    let compare = null;
    priced.forEach(function (variant) {
      const price = Number(variant.price);
      const compareAt = Number(variant.compare_at_price);
      if (price !== shown || !(compareAt > price)) return;
      const amount = compareAt - price;
      if (saved == null || amount < saved) {
        saved = amount;
        compare = compareAt;
      }
    });
    if (saved == null || saved <= 0 || !(compare > shown)) return null;
    const percent = Math.round((saved * 100) / compare);
    if (saveType === 'percent' && !(percent > 0)) return null;
    return { savedCents: saved, compareCents: compare, priceCents: shown, percent: percent };
  }

  // Size buttons are for one color. Multiple colors without a card color are skipped
  // so a tap cannot add an arbitrary color. Sold-out and $0 variants are skipped.
  function addableSizes(variants, options) {
    const list = Array.isArray(variants) ? variants : [];
    const sizeIndex = options ? options.sizeIndex : null;
    const colorIndex = options ? options.colorIndex : null;
    const colorValue = options ? options.colorValue : null;
    if (sizeIndex == null) return [];
    const colors = new Set(list.map(function (variant) {
      return colorIndex == null ? '' : variant.options && variant.options[colorIndex];
    }));
    if (colorIndex != null && (colorValue == null || colorValue === '') && colors.size > 1) return [];
    const seen = new Set();
    const buttons = [];
    list.forEach(function (variant) {
      if (!variant || !variant.options) return;
      if (colorIndex != null && colorValue != null && colorValue !== '' && variant.options[colorIndex] !== colorValue) return;
      const size = variant.options[sizeIndex];
      if (seen.has(size)) return;
      if (variant.available !== true || !(Number(variant.price) > 0)) return;
      seen.add(size);
      buttons.push({ id: variant.id, size: size, price: Number(variant.price), available: true });
    });
    return buttons;
  }

  // Reuses the builder's ?variant= handling. A specific card color, or a product with one
  // color, selects that variant. Several colors and no card color stay on the plain product
  // URL plus the builder hash, so a variant param is not invented.
  function familySetHref(productUrl, variants, options) {
    const list = Array.isArray(variants) ? variants : [];
    const colorIndex = options ? options.colorIndex : null;
    const colorValue = options && options.colorValue != null && options.colorValue !== '' ? options.colorValue : null;
    const scoped = colorValue == null ? list : list.filter(function (variant) {
      return variant.options && variant.options[colorIndex] === colorValue;
    });
    const colors = new Set(scoped.map(function (variant) {
      return colorIndex == null ? '' : variant.options && variant.options[colorIndex];
    }));
    let chosen = null;
    if (colorValue != null || colors.size <= 1) {
      chosen = scoped.find(function (variant) { return variant.available === true && Number(variant.price) > 0; })
        || scoped.find(function (variant) { return Number(variant.price) > 0; })
        || null;
    }
    const base = String(productUrl || '');
    if (!chosen) return base + '#matching-family';
    const join = base.indexOf('?') === -1 ? '?' : '&';
    return base + join + 'variant=' + chosen.id + '#matching-family';
  }

  function bindCardAdds(doc) {
    if (!doc || !doc.documentElement || doc.documentElement.dataset.leveretCardAdd === '1') return;
    doc.documentElement.dataset.leveretCardAdd = '1';
    doc.addEventListener('click', function (event) {
      const colorButton = event.target && event.target.closest && event.target.closest('[data-leveret-color]');
      if (colorButton) {
        event.preventDefault();
        event.stopPropagation();
        if (colorButton.disabled || colorButton.hasAttribute('data-sold-out')) return;
        const picker = colorButton.closest('[data-leveret-card-actions]');
        if (!picker) return;
        const color = colorButton.getAttribute('data-leveret-color');
        picker.querySelectorAll('[data-leveret-color]').forEach(function (item) {
          const on = item === colorButton;
          item.classList.toggle('is-active', on);
          item.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        picker.querySelectorAll('[data-leveret-size-group]').forEach(function (group) {
          group.hidden = group.getAttribute('data-leveret-size-group') !== color;
        });
        return;
      }
      const plus = event.target && event.target.closest && event.target.closest('[data-leveret-plus]');
      if (plus && !plus.hasAttribute('data-leveret-add')) {
        event.preventDefault();
        event.stopPropagation();
        const picker = plus.closest('[data-leveret-card-actions]');
        if (!picker) return;
        const sizes = picker.querySelector('.leveret-card-actions__sizes');
        const open = plus.getAttribute('aria-expanded') !== 'true';
        plus.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (sizes) sizes.hidden = !open;
        return;
      }
      const button = event.target && event.target.closest && event.target.closest('[data-leveret-add]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      const root = button.closest('[data-leveret-card-actions]');
      if (!root || root.dataset.busy === '1') return;
      const status = root.querySelector('[data-leveret-card-status]');
      let allowed = [];
      try { allowed = JSON.parse(root.querySelector('[data-leveret-addable]').textContent); } catch (error) { allowed = []; }
      const id = button.getAttribute('data-leveret-add');
      const variantId = Number(id);
      const quantity = 1;
      const row = allowed.find(function (variant) { return String(variant.id) === String(id); });
      if (!row || row.available !== true || !(Number(row.price) > 0) || !Number.isFinite(variantId) || variantId <= 0 || quantity <= 0) {
        if (status) status.textContent = 'That size is not available.';
        return;
      }
      const buttons = root.querySelectorAll('button');
      root.dataset.busy = '1';
      buttons.forEach(function (item) { item.disabled = true; });
      if (status) status.textContent = 'Adding ' + (row.size || 'that size') + '…';
      const url = root.getAttribute('data-cart-add') || '/cart/add.js';
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok || data.status === 422) throw new Error(data.description || data.message || 'Could not add that size.');
          return data;
        });
      }).then(function (data) {
        if (status) status.textContent = 'Added to cart.';
        doc.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data, addToCartBtn: button } }));
      }).catch(function (error) {
        if (status) status.textContent = error.message || 'Could not add that size.';
      }).finally(function () {
        root.dataset.busy = '';
        buttons.forEach(function (item) {
          if (!item.hasAttribute('data-sold-out')) item.disabled = false;
        });
      });
    });
    doc.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      const open = doc.querySelector('[data-leveret-plus][aria-expanded="true"]');
      if (!open) return;
      const picker = open.closest('[data-leveret-card-actions]');
      const sizes = picker && picker.querySelector('.leveret-card-actions__sizes');
      open.setAttribute('aria-expanded', 'false');
      if (sizes) sizes.hidden = true;
      open.focus();
    });
  }

  const api = { realCardSavings: realCardSavings, addableSizes: addableSizes, familySetHref: familySetHref, bindCardAdds: bindCardAdds };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (scope) scope.LeveretHolidayCards = Object.assign(scope.LeveretHolidayCards || {}, api);
  if (scope && scope.document) {
    if (scope.document.readyState === 'loading') scope.document.addEventListener('DOMContentLoaded', function () { bindCardAdds(scope.document); });
    else bindCardAdds(scope.document);
  }
})(typeof window !== 'undefined' ? window : undefined);
